import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

type FeedbackTokens = {
  question: string
  thumbSelectedBorder: string
  thumbSelectedColor: string
  thumbBorder: string
  thumbColor: string
  thumbHoverBg: string
  focusRing: string
  recordedIcon: string
  recordedLabel: string
}

// Semantic tokens only (design-library governance). Dark tokens at the top
// level; light counterparts under the `light.*` namespace.
const tokens: Record<ColorScheme, FeedbackTokens> = {
  dark: {
    question: 'text.secondary',
    thumbSelectedBorder: 'interactive.primary',
    thumbSelectedColor: 'interactive.primary',
    thumbBorder: 'interactive.tertiary-border',
    thumbColor: 'interactive.tertiary-text',
    thumbHoverBg: 'interactive.icon-button-hover-bg',
    focusRing: 'interactive.focus-ring',
    recordedIcon: 'status.success',
    recordedLabel: 'text.primary',
  },
  light: {
    question: 'light.text.secondary',
    thumbSelectedBorder: 'light.interactive.primary',
    thumbSelectedColor: 'light.interactive.primary',
    thumbBorder: 'light.interactive.tertiary-border',
    thumbColor: 'light.interactive.tertiary-text',
    thumbHoverBg: 'light.interactive.tertiary-hover-bg',
    focusRing: 'light.interactive.focus-ring',
    recordedIcon: 'light.status.success',
    recordedLabel: 'light.text.primary',
  },
}

// The top divider + top padding are supplied by the host card footer wrapper
// (see ReasoningTrail.styles cardFooterStyles), so this component owns only the
// stacked rows. No row gap here — the collapsible region owns its own top
// spacing so it disappears together with the content when collapsed.
export const containerStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  w: 'full',
}

// Animated reveal for the comment + submit. Follows the package's grid-rows
// pattern (see MultilineTextInput action slot): the outer grid animates
// 0fr → 1fr while the inner element clips its overflow, giving a smooth
// height transition without measuring. `visibility` keeps the collapsed
// content out of the tab order and the accessibility tree.
export const expandWrapperStyles = (
  isExpanded: boolean
): SystemStyleObject => ({
  display: 'grid',
  gridTemplateRows: isExpanded ? '1fr' : '0fr',
  transition: 'grid-template-rows 0.25s ease',
})

export const expandInnerStyles = (isExpanded: boolean): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  minH: 0,
  opacity: isExpanded ? 1 : 0,
  overflow: 'hidden',
  pt: '10px',
  transition: 'opacity 0.2s ease',
  visibility: isExpanded ? 'visible' : 'hidden',
})

export const thumbRowStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  w: 'full',
}

export const questionStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].question,
  flex: '1 0 0',
  m: 0,
  minW: 0,
  textStyle: 'caption',
  wordBreak: 'break-word',
})

export const thumbButtonStyles = (
  colorScheme: ColorScheme,
  isSelected: boolean
): SystemStyleObject => ({
  alignItems: 'center',
  bg: 'transparent',
  border: '1px solid',
  borderColor: isSelected
    ? tokens[colorScheme].thumbSelectedBorder
    : tokens[colorScheme].thumbBorder,
  // Drives the icon stroke via `currentColor`.
  color: isSelected
    ? tokens[colorScheme].thumbSelectedColor
    : tokens[colorScheme].thumbColor,
  cursor: 'pointer',
  display: 'flex',
  flexShrink: 0,
  h: '32px',
  justifyContent: 'center',
  overflow: 'hidden',
  transition:
    'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  w: '32px',
  _hover: { bg: tokens[colorScheme].thumbHoverBg },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: tokens[colorScheme].focusRing,
    outlineOffset: '2px',
  },
})

// space-between keeps the submit button right-aligned; the left slot holds the
// "feedback recorded" confirmation (or nothing before submit).
export const submitRowStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  w: 'full',
}

export const recordedStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  alignItems: 'center',
  color: tokens[colorScheme].recordedIcon,
  display: 'flex',
  gap: '7px',
})

export const recordedLabelStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].recordedLabel,
  m: 0,
  textStyle: 'caption',
  whiteSpace: 'nowrap',
})
